import {
    cloneElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    type MouseEvent,
    type PointerEvent,
    type ReactElement,
} from 'react';
import { Tooltip, type TooltipProps } from '@mui/material';

// K4: a tooltip for an info trigger that a finger can use. MUI opens a tooltip
// on a touch after a long press (or, with the delay removed, on the touch
// start, which also opens it when a scroll begins on the trigger). A tap
// belongs to the trigger, so here the tap itself decides:
//
//   finger    a tap opens it, a tap on the trigger again, a tap anywhere else
//             or a scroll closes it, and it closes by itself after 4 s. A
//             scroll that starts on the trigger sends no click, so it opens
//             nothing. The emulated mouse events a browser sends after a touch
//             are ignored (they would open it twice or close it at once).
//   mouse     exactly MUI's hover: opens after its delay, closes on leave. A
//             click neither closes it nor starts the 4 s timer.
//   keyboard  MUI's focus: a Tab to the trigger opens it, Escape and a Tab
//             away close it; Enter or Space on the trigger opens it too, for
//             a focus that was not a keyboard one.
//
// On a touch screen the bubble is the one the theme draws (touchTheme.ts): the
// K4 mockup's dark rounded bubble with an arrow. The trigger should be a
// control (a button, a chip with an onClick, an icon button) so that it is
// focusable and a tap on it reaches the click handler.

const AUTO_CLOSE_MS = 4000;

export interface TapTooltipProps
    extends Omit<
        TooltipProps,
        | 'open'
        | 'onOpen'
        | 'onClose'
        | 'children'
        | 'disableTouchListener'
        | 'enterTouchDelay'
        | 'leaveTouchDelay'
    > {
    /** The trigger: one element that takes a ref and the usual DOM props. */
    children: ReactElement;
    /** How long a tap-opened tooltip stays. */
    autoCloseMs?: number;
}

export function TapTooltip({
    children,
    autoCloseMs = AUTO_CLOSE_MS,
    arrow = true,
    describeChild = true,
    ...tooltip
}: TapTooltipProps) {
    const [open, setOpen] = useState(false);
    // The pointer that last touched the trigger: 'touch' or anything else.
    const pointer = useRef('mouse');
    const openedByTap = useRef(false);
    const trigger = useRef<Element | null>(null);

    const close = useCallback(() => {
        openedByTap.current = false;
        setOpen(false);
    }, []);

    // MUI reports a mouseover / mouseleave after every tap: not the user's.
    const emulated = (event: { type: string }) =>
        pointer.current === 'touch' && event.type.startsWith('mouse');

    useEffect(() => {
        if (!open || !openedByTap.current) return undefined;
        const timer = window.setTimeout(close, autoCloseMs);
        const onPointerDown = (event: Event) => {
            const target = event.target;
            if (
                !(target instanceof Node) ||
                !trigger.current?.contains(target)
            ) {
                close();
            }
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('scroll', close, {
            capture: true,
            passive: true,
        });
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('scroll', close, true);
        };
    }, [open, autoCloseMs, close]);

    const own = children.props as {
        onClick?: (event: MouseEvent<HTMLElement>) => void;
        onPointerDownCapture?: (event: PointerEvent<HTMLElement>) => void;
        onPointerEnter?: (event: PointerEvent<HTMLElement>) => void;
    };

    return (
        <Tooltip
            {...tooltip}
            arrow={arrow}
            describeChild={describeChild}
            open={open}
            disableTouchListener
            onOpen={event => {
                if (emulated(event)) return;
                openedByTap.current = false;
                setOpen(true);
            }}
            onClose={event => {
                if (emulated(event)) return;
                close();
            }}
        >
            {cloneElement(children, {
                onPointerDownCapture: (event: PointerEvent<HTMLElement>) => {
                    pointer.current = event.pointerType;
                    own.onPointerDownCapture?.(event);
                },
                onPointerEnter: (event: PointerEvent<HTMLElement>) => {
                    pointer.current = event.pointerType;
                    own.onPointerEnter?.(event);
                },
                onClick: (event: MouseEvent<HTMLElement>) => {
                    own.onClick?.(event);
                    trigger.current = event.currentTarget;
                    if (pointer.current === 'touch') {
                        if (open) {
                            close();
                        } else {
                            openedByTap.current = true;
                            setOpen(true);
                        }
                    } else if (event.detail === 0) {
                        // Enter or Space on the trigger
                        setOpen(true);
                    }
                },
            })}
        </Tooltip>
    );
}
